import { NetworkStatus, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useEffect, useMemo, useRef } from 'react';
import { isNullish } from 'remeda';
import { mapNotificationRecord } from 'src/graphql/entities/notification/mappings';
import { GET_NOTIFICATIONS_QUERY } from 'src/graphql/entities/notification/notification.operations';
import { NotificationStatus } from 'src/graphql/entities/notification/notification.types';
import {
  buildNotificationCompositeKey,
  getBaseNotificationFilters,
} from 'src/graphql/entities/notification/utils';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { QueryParams, QueryVariables, Records } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { NotificationRaw } from 'src/types';
import { sortByInput } from 'src/utils/sorting';

export type GetNotification = {
  getNotification: Records<NotificationRaw>;
};

const NOTIFICATION_PAGE_LIMIT = 50;

export const MAX_NOTIFICATIONS_LIMIT = 1000;

export const useNotificationRecords = (
  params?: {
    filters?: QueryParams['filters'];
    sorting?: QueryParams['sorting'];
  },
  options?: QueryHookOptions<GetNotification, QueryVariables>,
) => {
  const { user } = useCurrentUserProfile();

  const {
    data: rawData,
    fetchMore,
    previousData,
    variables,
    loading,
    networkStatus,
    ...rest
  } = useQuery<GetNotification, QueryVariables>(GET_NOTIFICATIONS_QUERY, {
    ...options,
    variables: {
      params: {
        limit: NOTIFICATION_PAGE_LIMIT,
        filters: [...getBaseNotificationFilters(user?.id!), ...(params?.filters ?? [])],
        sorting: params?.sorting || { attribute: 'createdAt', desc: true },
      },
    },
    notifyOnNetworkStatusChange: true,
    pollInterval: 3_600_000,
    skip: !user?.id,
  });

  const data = rawData || previousData;

  const records = data?.getNotification?.records;

  // an empty list that has already come back from the server is indistinguishable
  // from "nothing fetched yet" by `records` alone, so a re-run of the query (the
  // ws-driven invalidation) would put the skeletons back over a resolved empty
  // inbox. Remember that these variables have settled and only skeleton the first load.
  const settledParamsKey = JSON.stringify(variables?.params ?? null);
  const settledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!loading && rawData) {
      settledKeyRef.current = settledParamsKey;
    }
  }, [loading, rawData, settledParamsKey]);

  const { loadMore, hasReachedEnd } = useLoadMore<GetNotification>({
    offset: records?.length ?? 0,
    fetchMore,
    params: variables?.params!,
  });

  const notifications = useMemo(() => {
    if (!records) return [];

    // map and filter out archived, then sort
    const mapped = sortByInput(
      records
        .map((i) => mapNotificationRecord(i))
        .filter((i) => i.status !== NotificationStatus.Archived),
      variables?.params?.sorting,
    );

    // keep only the latest notification per entity+entityId
    const seen = new Set<string>();
    const grouped: typeof mapped = [];

    for (const n of mapped) {
      const key = buildNotificationCompositeKey(n.entity, n.entityId);

      if (!seen.has(key)) {
        seen.add(key);
        grouped.push(n);
      }
    }

    return grouped;
  }, [records, variables?.params?.sorting]);

  // Reflect the grouped count in the UI; we only show latest per entityId
  const totalRecords = notifications?.length ?? 0;
  // a count of 0 is an answer, not a gap: only a skipped count (follow-up pages) falls
  // back to probing. Treating 0 as unknown made the just-emptied inbox — whose cached
  // rows are all archived, so the pane shows none — ask for a page it could never get.
  // And with no page at all there is nothing to page from: the sentinel is already in
  // view on the empty pane, so a fetchMore would go out before the first page did
  const serverTotalRecords = data?.getNotification?.metadata?.totalRecords;
  const hasMore =
    !!data &&
    (!isNullish(serverTotalRecords)
      ? (records?.length ?? 0) < Math.min(serverTotalRecords, MAX_NOTIFICATIONS_LIMIT)
      : !hasReachedEnd);

  return {
    ...rest,
    notifications,
    loadMore,
    hasMore,
    isLoading: loading && !records?.length && settledKeyRef.current !== settledParamsKey,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    totalRecords,
  };
};
